import React, { useEffect, useState } from 'react';
import { api, FILES_BASE } from './AdminApp';
import Modal from './Modal';
import Loader from '../Components/Loader/Loader';

const TABS = [
  ['appointments', 'Appointments'],
  ['contacts', 'Contact Messages'],
  ['jobs', 'Job Applications'],
  ['subscribers', 'Subscribers'],
];

// kind -> { column key, options } — subscribers use a different pipeline.
const STATUS = {
  default: {
    col: 'lead_status',
    options: [
      ['lead', 'Lead'],
      ['in_progress', 'In Progress'],
      ['completed', 'Completed'],
    ],
  },
  subscribers: {
    col: 'status',
    options: [
      ['subscribed', 'Subscribed'],
      ['unsubscribed', 'Unsubscribed'],
    ],
  },
};

const COLUMNS = {
  appointments: [
    ['id', 'ID'],
    ['name', 'Name'],
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['appointment_date', 'Date'],
    ['message', 'Message'],
    ['created_at', 'Received'],
  ],
  contacts: [
    ['id', 'ID'],
    ['name', 'Name'],
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['service', 'Service'],
    ['message', 'Message'],
    ['created_at', 'Received'],
  ],
  jobs: [
    ['id', 'ID'],
    ['name', 'Name'],
    ['email', 'Email'],
    ['phone', 'Phone'],
    ['job_title', 'Job'],
    ['resume', 'Resume'],
    ['created_at', 'Received'],
  ],
  subscribers: [
    ['id', 'ID'],
    ['email', 'Email'],
    ['source', 'Source'],
    ['subscribed_at', 'Subscribed'],
  ],
};

function fmt(v) {
  if (v == null) return '—';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) || /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) {
    return s.replace('T', ' ').slice(0, 16);
  }
  if (s.startsWith('uploads/')) {
    return s; // handled by the cell renderer as a download link
  }
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
}

function fmtNoteTime(v) {
  if (!v) return '';
  const d = new Date(String(v).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function Leads() {
  const [tab, setTab] = useState('appointments');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  // Notes modal state
  const [notesFor, setNotesFor] = useState(null); // { kind, id, label }
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [notesBusy, setNotesBusy] = useState(false);

  const statusCfg = tab === 'subscribers' ? STATUS.subscribers : STATUS.default;
  const statusCol = statusCfg.col;

  const load = async (kind, opts = {}) => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      q.set('page', String(opts.page ?? page));
      q.set('per_page', String(opts.perPage ?? perPage));
      const s = opts.search !== undefined ? opts.search : search;
      if (s) q.set('search', s);
      const st = opts.status !== undefined ? opts.status : statusFilter;
      if (st) q.set('status', st);
      const d = await api(`/admin/leads/${kind}?${q.toString()}`);
      setItems(d.items || []);
      setPage(d.page || 1);
      setTotal(d.total || 0);
      setTotalPages(d.total_pages || 1);
      setSelected(new Set());
      setError('');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load(tab, { page: 1, search: searchInput, status: statusFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const changeTab = (kind) => {
    setTab(kind);
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setPage(1);
    load(kind, { page: 1, search: '', status: '' });
  };

  const doSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
    load(tab, { page: 1, search: searchInput });
  };

  const filterByStatus = (st) => {
    setStatusFilter(st);
    setPage(1);
    load(tab, { page: 1, status: st });
  };

  const goto = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    load(tab, { page: p });
  };

  const changePerPage = (n) => {
    setPerPage(n);
    setPage(1);
    load(tab, { page: 1, perPage: n });
  };

  const setStatus = async (ids, status) => {
    setError('');
    setNotice('');
    try {
      const res = await api('/admin/leads/status', {
        method: 'POST',
        body: JSON.stringify({ kind: tab, ids, status }),
      });
      setNotice(res.message || 'Updated.');
      load(tab, {});
    } catch (e) {
      setError(e.message);
    }
  };

  const openNotes = async (row) => {
    setNotesFor({ kind: tab, id: row.id, label: row.name || row.email || `#${row.id}` });
    setNotes([]);
    setNewNote('');
    setNotesBusy(true);
    try {
      const d = await api(`/admin/leads/notes?kind=${tab}&lead_id=${row.id}`);
      setNotes(d.notes || []);
    } catch (e) {
      setError(e.message);
    }
    setNotesBusy(false);
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || !notesFor) return;
    setNotesBusy(true);
    setError('');
    try {
      const d = await api('/admin/leads/notes', {
        method: 'POST',
        body: JSON.stringify({ kind: notesFor.kind, lead_id: notesFor.id, note: newNote.trim() }),
      });
      if (d.note) setNotes((prev) => [d.note, ...prev]);
      setNewNote('');
      // refresh the count badge in the table
      setItems((prev) =>
        prev.map((r) => (r.id === notesFor.id ? { ...r, note_count: (r.note_count || 0) + 1 } : r))
      );
    } catch (err) {
      setError(err.message);
    }
    setNotesBusy(false);
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => (prev.size === items.length ? new Set() : new Set(items.map((r) => r.id))));
  };

  const cols = COLUMNS[tab];

  const pageNumbers = () => {
    const out = [];
    const span = 2;
    for (let p = 1; p <= totalPages; p++) {
      if (p === 1 || p === totalPages || Math.abs(p - page) <= span) out.push(p);
      else if (out[out.length - 1] !== '…') out.push('…');
    }
    return out;
  };

  const statusClass = (v) =>
    ({
      lead: 'admin-status admin-status-lead',
      in_progress: 'admin-status admin-status-progress',
      completed: 'admin-status admin-status-completed',
      subscribed: 'admin-status admin-status-completed',
      unsubscribed: 'admin-status admin-status-muted',
    }[v] || 'admin-status');

  return (
    <div>
      <h1 className="admin-page-title">Leads</h1>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {notice && <div className="admin-alert admin-alert-success">{notice}</div>}

      <div className="admin-tabs">
        {TABS.map(([kind, label]) => (
          <button
            key={kind}
            className={`admin-tab ${tab === kind ? 'active' : ''}`}
            onClick={() => changeTab(kind)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="admin-leads-toolbar">
        <form className="admin-leads-search" onSubmit={doSearch}>
          <input
            placeholder="Search name, email, phone…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className="admin-btn">Search</button>
          {search && (
            <button
              type="button"
              className="admin-btn"
              title="Clear search"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setPage(1);
                load(tab, { page: 1, search: '' });
              }}
            >
              ×
            </button>
          )}
        </form>

        <div className="admin-leads-filters">
          <span className="admin-muted" style={{ fontSize: 13 }}>Status:</span>
          <button
            className={`admin-chip ${statusFilter === '' ? 'active' : ''}`}
            onClick={() => filterByStatus('')}
          >
            All
          </button>
          {statusCfg.options.map(([v, label]) => (
            <button
              key={v}
              className={`admin-chip ${statusFilter === v ? 'active' : ''}`}
              onClick={() => filterByStatus(v)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="admin-leads-count">
          {loading ? '…' : `${total} total`}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="admin-bulkbar">
          <strong>{selected.size}</strong>&nbsp;selected — change status to:
          {statusCfg.options.map(([v, label]) => (
            <button
              key={v}
              className="admin-btn admin-btn-sm"
              onClick={() => setStatus(Array.from(selected), v)}
            >
              {label}
            </button>
          ))}
          <button className="admin-btn admin-btn-sm" onClick={() => setSelected(new Set())}>
            Clear selection
          </button>
        </div>
      )}

      {/* Notes modal */}
      {notesFor && (
        <Modal
          title={`Notes — ${notesFor.label}`}
          onClose={() => setNotesFor(null)}
          wide
        >
          <form className="admin-form" onSubmit={addNote}>
            <label className="admin-form-full">
              Add a note
              <textarea
                rows={3}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="e.g. Called the patient, will visit on Friday…"
              />
            </label>
            <div className="admin-form-actions" style={{ marginBottom: 18 }}>
              <button type="submit" className="admin-btn admin-btn-primary" disabled={notesBusy || !newNote.trim()}>
                {notesBusy ? 'Saving…' : 'Add note'}
              </button>
            </div>
          </form>

          {notesBusy && notes.length === 0 ? (
            <Loader inline status="Loading notes" />
          ) : notes.length === 0 ? (
            <p className="admin-muted">No notes yet — add the first one above.</p>
          ) : (
            <div className="admin-notes-list">
              {notes.map((n) => (
                <div className="admin-note-item" key={n.id}>
                  <div className="admin-note-meta">
                    <strong>{n.username}</strong>
                    <span>{fmtNoteTime(n.created_at)}</span>
                  </div>
                  <div className="admin-note-text">{n.note}</div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {loading ? (
        <Loader inline status="Loading leads" />
      ) : items.length === 0 ? (
        <p className="admin-muted">No entries{search || statusFilter ? ' matching the filters' : ' yet'}.</p>
      ) : (
        <>
          <table className="admin-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selected.size === items.length && items.length > 0}
                    onChange={toggleAll}
                  />
                </th>
                {cols.map(([k, label]) => (
                  <th key={k}>{label}</th>
                ))}
                <th>Notes</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className={selected.has(row.id) ? 'admin-row-selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                    />
                  </td>
                  {cols.map(([k]) => {
                    const v = row[k];
                    const isFile = typeof v === 'string' && v.startsWith('uploads/');
                    return (
                      <td key={k} title={String(v ?? '')}>
                        {isFile ? (
                          <a
                            href={`${FILES_BASE}/${v}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                          >
                            📄 Download
                          </a>
                        ) : (
                          fmt(v)
                        )}
                      </td>
                    );
                  })}
                  <td>
                    <button
                      className="admin-notes-btn"
                      title="View / add notes"
                      onClick={() => openNotes(row)}
                    >
                      📝 {row.note_count || 0}
                    </button>
                  </td>
                  <td>
                    <select
                      className={statusClass(row[statusCol])}
                      value={row[statusCol]}
                      onChange={(e) => setStatus([row.id], e.target.value)}
                    >
                      {statusCfg.options.map(([v, label]) => (
                        <option key={v} value={v}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

      <div className="admin-pagination">
            <button
              className="admin-btn admin-btn-sm"
              disabled={page <= 1}
              onClick={() => goto(page - 1)}
            >
              ← Prev
            </button>
            {pageNumbers().map((p, i) =>
              p === '…' ? (
                <span key={`e${i}`} className="admin-page-ellipsis">…</span>
              ) : (
                <button
                  key={p}
                  className={`admin-page-btn ${p === page ? 'active' : ''}`}
                  onClick={() => goto(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              className="admin-btn admin-btn-sm"
              disabled={page >= totalPages}
              onClick={() => goto(page + 1)}
            >
              Next →
            </button>

            <select
              className="admin-perpage"
              value={perPage}
              onChange={(e) => changePerPage(Number(e.target.value))}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  );
}
